/**
 * Phase P1+ (2026-05-15) — Manual per-article promotion modal.
 *
 * Three promo types:
 *   - percent       (10% off → prix_vente * 0.9)
 *   - fixed_amount  (-5€ → prix_vente - 5)
 *   - fixed_price   (forced price → value as-is)
 *
 * Live preview of "Prix actuel → Nouveau prix". Warns if an active promo
 * already exists. On submit, the backend archives the old one (R1 soft).
 *
 * Props:
 *   article     - { id, designation, prix_vente, ean, ... }
 *   open        - bool
 *   onClose     - () => void
 *   onCreated   - (response) => void   // refresh parent + offer label print
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Percent, Tag, BadgeDollarSign, AlertTriangle, CheckCircle2, Loader2, Printer,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TYPES = [
  { id: 'percent', label: 'Pourcentage', icon: Percent, suffix: '%', help: 'Réduction proportionnelle au prix de vente' },
  { id: 'fixed_amount', label: 'Montant fixe à déduire', icon: BadgeDollarSign, suffix: '€', help: 'Réduit le prix par une somme en euros' },
  { id: 'fixed_price', label: 'Prix imposé', icon: Tag, suffix: '€', help: 'Le client paye exactement cette somme' },
];

const computePreview = (base, type, value) => {
  const v = parseFloat(value);
  if (!Number.isFinite(v)) return null;
  if (type === 'percent') {
    if (v <= 0 || v > 100) return null;
    return Math.max(0, +(base * (1 - v / 100)).toFixed(2));
  }
  if (type === 'fixed_amount') {
    if (v < 0) return null;
    return Math.max(0, +(base - v).toFixed(2));
  }
  if (type === 'fixed_price') {
    if (v < 0) return null;
    return +Number(v).toFixed(2);
  }
  return null;
};

const ArticlePromotionModal = ({ article, open, onClose, onCreated }) => {
  const { api } = useAuth();
  const [type, setType] = useState('percent');
  const [value, setValue] = useState('10');
  const [label, setLabel] = useState('Promo');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open || !article?.id) return;
    setExisting(null);
    setCreated(null);
    setError('');
    setValue('10'); setType('percent'); setLabel('Promo');
    setStartsAt(''); setEndsAt('');
    api.get(`/stock/articles/${article.id}/promotions`).then((r) => {
      setExisting(r.data?.active || null);
    }).catch(() => {});
  }, [open, article?.id, api]);

  const base = parseFloat(article?.prix_vente || 0);
  const preview = useMemo(() => computePreview(base, type, value), [base, type, value]);
  const diff = preview !== null ? +(base - preview).toFixed(2) : null;
  const diffPct = preview !== null && base > 0 ? +((diff / base) * 100).toFixed(1) : null;

  if (!open) return null;

  const submit = async () => {
    setError('');
    if (preview === null) { setError('Valeur invalide pour ce type de promo.'); return; }
    if (preview >= base && type !== 'fixed_price') {
      setError("Le prix après promo n'est pas inférieur au prix de base.");
      return;
    }
    setBusy(true);
    try {
      const idem = (window.crypto?.randomUUID?.()) || `promo-${Date.now()}`;
      const res = await api.post(
        `/stock/articles/${article.id}/promotions`,
        {
          type,
          value: parseFloat(value),
          label: label || 'Promo',
          starts_at: startsAt || null,
          ends_at: endsAt || null,
        },
        { headers: { 'Idempotency-Key': idem } },
      );
      setCreated(res.data);
      onCreated?.(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la création de la promo.');
    } finally {
      setBusy(false);
    }
  };

  const printLabel = async () => {
    try {
      await api.post('/stock/labels/print', { items: [{ product_id: article.id, quantity: 1 }] });
    } catch {
      // The print manager error already surfaces a toast elsewhere; ignore here.
    }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="article-promo-modal">
      <div className="w-full max-w-lg bg-gray-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-400" />
              {created ? 'Promo activée' : 'Ajouter une promotion'}
            </h2>
            <p className="text-xs text-gray-500 truncate max-w-[400px]">{article?.designation}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5" data-testid="promo-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        {!created ? (
          <div className="p-4 space-y-4">
            {existing && (
              <div className="bg-amber-500/15 border border-amber-500/40 rounded-xl p-3 text-amber-100 text-xs flex items-start gap-2" data-testid="promo-existing-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <div>
                  Une promo est déjà active sur cet article : <strong>{existing.label}</strong>{' '}
                  ({existing.type === 'percent' ? `-${existing.value}%` : existing.type === 'fixed_amount' ? `-${existing.value}€` : `${existing.value}€`}).
                  En continuant, l'ancienne sera archivée automatiquement.
                </div>
              </div>
            )}

            {/* Type */}
            <div>
              <div className="text-xs uppercase text-gray-500 mb-2 tracking-wider">Type de réduction</div>
              <div className="grid grid-cols-1 gap-2">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = type === t.id;
                  return (
                    <button key={t.id} onClick={() => setType(t.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                        active ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-100'
                               : 'bg-white/[0.03] border-white/10 text-gray-300 hover:bg-white/[0.06]'
                      }`}
                      data-testid={`promo-type-${t.id}`}>
                      <Icon className="w-5 h-5 shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t.label}</div>
                        <div className="text-[11px] text-gray-500">{t.help}</div>
                      </div>
                      {active && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Value + label */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase text-gray-500 tracking-wider">Valeur</label>
                <div className="flex items-center gap-1 mt-1">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white"
                    data-testid="promo-value"
                  />
                  <span className="text-sm text-gray-400 w-6">{TYPES.find((t) => t.id === type)?.suffix}</span>
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase text-gray-500 tracking-wider">Libellé</label>
                <input
                  type="text"
                  maxLength={60}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="ex: Soldes janvier"
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white"
                  data-testid="promo-label"
                />
              </div>
            </div>

            {/* Live preview */}
            <div className="bg-white/[0.04] border border-white/10 rounded-xl p-3 text-sm" data-testid="promo-preview">
              <div className="text-[11px] uppercase text-gray-500 tracking-wider mb-1">Aperçu</div>
              {preview !== null ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-500 line-through">{base.toFixed(2)} €</span>
                  <span className="text-emerald-300 font-bold text-lg">{preview.toFixed(2)} €</span>
                  {diff !== null && diff > 0 && (
                    <span className="text-amber-300 text-xs">économie {diff.toFixed(2)} € (−{diffPct}%)</span>
                  )}
                </div>
              ) : (
                <span className="text-gray-500 text-xs">Saisissez une valeur valide…</span>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase text-gray-500 tracking-wider">Début (optionnel)</label>
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full mt-1 px-2 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
                  data-testid="promo-starts-at"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase text-gray-500 tracking-wider">Fin (optionnel)</label>
                <input
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  className="w-full mt-1 px-2 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
                  data-testid="promo-ends-at"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-500/15 border border-red-500/30 rounded-xl p-3 text-red-200 text-sm" data-testid="promo-error">
                {error}
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-3" data-testid="promo-created-summary">
            <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-xl p-3 text-emerald-100 text-sm flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Promotion activée : <strong>{created.promo?.label}</strong> ({created.promo?.type === 'percent'
                ? `-${created.promo?.value}%`
                : created.promo?.type === 'fixed_amount'
                  ? `-${created.promo?.value}€`
                  : `${created.promo?.value}€`}).
            </div>
            <div className="text-sm text-gray-300">
              Nouveau prix : <strong className="text-emerald-300">{created.article?.prix_vente_effectif?.toFixed(2)} €</strong>{' '}
              <span className="text-gray-500 line-through ml-2">{created.article?.prix_vente?.toFixed(2)} €</span>
            </div>
          </div>
        )}

        <footer className="p-4 border-t border-white/10 flex items-center justify-between gap-3">
          {!created ? (
            <>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm" data-testid="promo-cancel">
                Annuler
              </button>
              <button onClick={submit} disabled={busy || preview === null}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2"
                data-testid="promo-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                Activer la promo
              </button>
            </>
          ) : (
            <>
              <button onClick={printLabel} className="px-4 py-2 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/25 text-sm flex items-center gap-2" data-testid="promo-print-label">
                <Printer className="w-4 h-4" /> Imprimer l'étiquette promo
              </button>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 text-sm font-medium" data-testid="promo-done">
                Terminé
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ArticlePromotionModal;
